import type { DesignPreferenceItemFormatted } from "../types/designPreferences";
import { getFieldKey } from "./groupDesignPreferences";
import type { ViewMode } from "checkout/types/payments-ui-form";

/** Parent question titles that use custom rules for when child fields appear (not the default Yes gate). */
const PARENT_QUESTION_PRIMARY_GOAL = "Primary Goal";
const PARENT_QUESTION_ROOFS_TO_BE_USED = "Roofs to be used";

/** Primary Goal: follow-up fields show only for these selections. */
const PRIMARY_GOAL_SHOWS_CHILDREN = {
  AESTHETIC_PREFERENCES: "Aesthetic Preferences",
  OTHER: "Other",
} as const;

/** Roofs to be used: free-text follow-up shows only when this option is selected. */
const AVOID_CERTAIN_ROOFS = "Avoid Certain Roof";

/** Default: child fields appear when the parent answer is exactly this (e.g. HOA “Is HOA Applicable?”). */
const PARENT_ANSWER_SHOWS_CHILDREN_DEFAULT = "Yes";

/**
 * When the parent’s answer matches one of these rules, dependent child questions are shown
 * (and treated as required until filled—see `isEffectivelyRequired`).
 */
function doesParentAnswerRevealChildFields(
  parentQuestionTitle: string,
  parentAnswer: string
): boolean {
  if (parentQuestionTitle === PARENT_QUESTION_PRIMARY_GOAL) {
    const isAestheticOrOther =
      parentAnswer === PRIMARY_GOAL_SHOWS_CHILDREN.AESTHETIC_PREFERENCES ||
      parentAnswer === PRIMARY_GOAL_SHOWS_CHILDREN.OTHER;
    return isAestheticOrOther;
  }

  if (parentQuestionTitle === PARENT_QUESTION_ROOFS_TO_BE_USED) {
    return parentAnswer === AVOID_CERTAIN_ROOFS;
  }

  return parentAnswer === PARENT_ANSWER_SHOWS_CHILDREN_DEFAULT;
}

function findParentField(
  field: DesignPreferenceItemFormatted,
  allFieldsInGroup: DesignPreferenceItemFormatted[]
): DesignPreferenceItemFormatted | undefined {
  if (!field.parentQuestion) {
    return undefined;
  }
  return allFieldsInGroup.find((f) => f.question === field.parentQuestion);
}

/**
 * Whether this field should be rendered: every ancestor must be visible first,
 * then the immediate parent’s answer must pass `doesParentAnswerRevealChildFields`.
 */
function shouldShowDesignPreferenceField(
  field: DesignPreferenceItemFormatted,
  allFieldsInGroup: DesignPreferenceItemFormatted[],
  getValue: (item: DesignPreferenceItemFormatted) => string
): boolean {
  const parent = findParentField(field, allFieldsInGroup);
  if (!parent) {
    return true;
  }

  const parentIsVisible = shouldShowDesignPreferenceField(
    parent,
    allFieldsInGroup,
    getValue
  );
  if (!parentIsVisible) {
    return false;
  }

  return doesParentAnswerRevealChildFields(parent.question, getValue(parent));
}

/** Fields that count toward “fill this card” / Continue (headers and display-only rows excluded). */
function isFillableDesignPreferenceField(
  field: DesignPreferenceItemFormatted
): boolean {
  const isHeaderOrSubHeader =
    field.respDataType === "Header" || field.respDataType === "SubHeader";
  const isPhoneOrOutputText =
    field.respDataType === "Phone" || field.respDataType === "OutputText";
  return !isHeaderOrSubHeader && !isPhoneOrOutputText;
}

/**
 * Required if marked required by the API, or if this field is a visible child of a parent
 * whose current answer triggers child fields (`shouldShowDesignPreferenceField`).
 */
function isEffectivelyRequired(
  field: DesignPreferenceItemFormatted,
  allFieldsInGroup: DesignPreferenceItemFormatted[],
  getValue: (item: DesignPreferenceItemFormatted) => string
): boolean {
  if (field.isRequired) {
    return true;
  }
  if (!isFillableDesignPreferenceField(field)) {
    return false;
  }
  if (!field.parentQuestion) {
    return false;
  }
  return shouldShowDesignPreferenceField(field, allFieldsInGroup, getValue);
}

function getDesignPreferenceValue(
  field: DesignPreferenceItemFormatted,
  viewMode: ViewMode,
  fieldIndices: Map<DesignPreferenceItemFormatted, number>,
  formValues: Map<string, string>
): string {
  if (viewMode === "view") {
    return field.response ?? "";
  }
  const index = fieldIndices.get(field);
  if (index === undefined) {
    return field.response ?? "";
  }
  const key = getFieldKey(field, index);
  return formValues.get(key) ?? field.response ?? "";
}

export {
  shouldShowDesignPreferenceField,
  isFillableDesignPreferenceField,
  isEffectivelyRequired,
  getDesignPreferenceValue,
  PARENT_QUESTION_PRIMARY_GOAL,
  PARENT_QUESTION_ROOFS_TO_BE_USED,
  PRIMARY_GOAL_SHOWS_CHILDREN,
  AVOID_CERTAIN_ROOFS,
  PARENT_ANSWER_SHOWS_CHILDREN_DEFAULT,
};
